import { useState, useEffect, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Assessment, Category } from '@/types/assessment';
import { useAssessmentProgress } from '@/hooks/useAssessmentProgress';
import { useAssessmentResponses } from '@/hooks/useAssessmentResponses';
import { calculateProgressPercentage, isQuestionFullyAnswered } from '@/utils/assessmentHelpers';
import { validateAllRequiredQuestionsAnswered } from '@/hooks/assessment/responseUtils';
import AssessmentLayout from '@/components/assessment-portal/AssessmentLayout';
import AssessmentProgress from '@/components/assessment-portal/AssessmentProgress';
import AssessmentContent from '@/components/assessment-portal/AssessmentContent';
import PremiumAssessmentHeader from '@/components/premium-dashboard/PremiumAssessmentHeader';
import { supabase } from '@/integrations/supabase/client';
import { buildPrefilledResponsesFromAnswerDetails } from './premiumAssessmentPrefill';
import { deletePremiumProgress } from '@/services/premiumProgressService';

interface PremiumAssessmentProps {
  assessment: Assessment;
  onBack: () => void;
  startFresh?: boolean;
}

/**
 * Premium assessment flow: includes ALL questions (free + premium, non-disabled).
 * Free questions appear first in each category, then premium questions.
 * The latest free assessment responses are pre-filled.
 */
const PremiumAssessment = ({ assessment, onBack, startFresh = false }: PremiumAssessmentProps) => {
  const [userEmail, setUserEmail] = useState('');
  const [freeResponsesLoaded, setFreeResponsesLoaded] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.email) setUserEmail(user.email);
    });
    // Only clear progress when starting fresh (not resuming)
    if (startFresh && assessment?.id) {
      localStorage.removeItem(`assessment_${assessment.id}_responses`);
      localStorage.removeItem(`assessment-progress-${assessment.id}`);
      localStorage.removeItem(`assessment-progress-premium-${assessment.id}`);
      deletePremiumProgress(assessment.id).catch(() => {});
    }
  }, []);

  // Get all categories with all non-disabled questions (free first, then premium)
  const sortedCategories = useMemo(() => getAllCategoriesWithQuestions(assessment), [assessment]);

  const {
    currentStep,
    currentCategoryIndex,
    currentQuestionIndex,
    currentCategory,
    currentQuestion,
    personalInfo,
    setPersonalInfo,
    handleStartAssessment,
    handleRestartAssessment,
    moveToNextQuestion,
    moveToPreviousQuestion,
    moveBackToQuestions,
    navigateToQuestion,
    completeAssessment,
  } = useAssessmentProgress({ assessment, categories: sortedCategories });

  const {
    responses,
    calculatedScores,
    handleResponseChange,
    canMoveForward,
    submitAssessment,
    isSubmitting,
    clearAllResponses,
    batchSetResponses,
  } = useAssessmentResponses({ assessment, categories: sortedCategories });

  // Pre-fill with latest free assessment responses (only when starting fresh)
  useEffect(() => {
    if (!userEmail || freeResponsesLoaded || !startFresh) return;

    const fetchFreeResponses = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await supabase.functions.invoke('get-my-responses', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (response.data && Array.isArray(response.data)) {
          // Find the latest response for this assessment
          const latestResponse = response.data.find(
            (r: any) => r.assessmentId === assessment.id || r.assessmentSlug === assessment.slug
          );

          if (latestResponse?.answerDetails) {
            const prefilled = buildPrefilledResponsesFromAnswerDetails(
              latestResponse.answerDetails,
              sortedCategories
            );
            
            if (Object.keys(prefilled).length > 0) {
              console.log('[PremiumAssessment] Pre-filling', Object.keys(prefilled).length, 'responses');
              batchSetResponses(prefilled);
            }
          }
        }
      } catch (err) {
        console.error('[PremiumAssessment] Error fetching free responses:', err);
      } finally {
        setFreeResponsesLoaded(true);
      }
    };

    fetchFreeResponses();
  }, [userEmail, assessment.id, assessment.slug, freeResponsesLoaded, sortedCategories]);

  const handleFullRestart = () => {
    clearAllResponses();
    handleRestartAssessment();
  };

  const totalQuestions = sortedCategories.reduce((sum, c) => sum + c.questions.length, 0);
  const completedCount = Object.values(responses).filter((r) => isQuestionFullyAnswered(r)).length;
  const progressPercentage = calculateProgressPercentage(totalQuestions, completedCount);

  const currentCategoryQuestionsCount = currentCategory ? currentCategory.questions.length : 0;
  const currentCategoryCompletedCount = currentCategory
    ? currentCategory.questions.filter((q) => isQuestionFullyAnswered(responses[q.id])).length
    : 0;

  const handleMoveToNextQuestion = async () => {
    // Check if this is the last question
    const isLastCategory = currentCategoryIndex >= sortedCategories.length - 1;
    const isLastQuestion = currentCategory && currentQuestionIndex >= currentCategory.questions.length - 1;
    
    if (isLastCategory && isLastQuestion) {
      // Validate all required questions before submitting
      const validation = validateAllRequiredQuestionsAnswered(responses, sortedCategories);
      if (!validation.valid) {
        toast({
          variant: "destructive",
          title: "Incomplete Assessment",
          description: `Please answer all required questions before submitting. (${validation.unansweredQuestions.length} questions remaining)`
        });
        return;
      }
      // For premium: skip personalInfo, auto-submit with auth user data
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const { data: orgMember } = await supabase
          .from('organization_members')
          .select('full_name, email')
          .eq('id', user?.id || '')
          .maybeSingle();
        
        const autoInfo = {
          fullName: orgMember?.full_name || user?.user_metadata?.full_name || '',
          email: user?.email || '',
          company: '',
          designation: '',
          consentGiven: true,
        };
        
        setPersonalInfo(autoInfo);
        const success = await submitAssessment(autoInfo);
        if (success) {
          // Clean up progress records so dashboard shows "completed" not "in_progress"
          localStorage.removeItem(`assessment_${assessment.id}_responses`);
          localStorage.removeItem(`assessment-progress-${assessment.id}`);
          localStorage.removeItem(`assessment-progress-premium-${assessment.id}`);
          deletePremiumProgress(assessment.id).catch(() => {});
          completeAssessment();
        }
      } catch (error) {
        console.error('[PremiumAssessment] Error during auto-submission:', error);
      }
    } else {
      moveToNextQuestion(responses);
    }
  };

  const handleNavigateToQuestion = (categoryIndex: number, questionIndex: number) => {
    navigateToQuestion(categoryIndex, questionIndex);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PremiumAssessmentHeader
        assessmentTitle={assessment.title}
        userEmail={userEmail}
        onGoHome={onBack}
      />
      <div className="flex-1 flex flex-col">
        <AssessmentLayout
          assessment={assessment}
          categories={sortedCategories}
          currentCategoryIndex={currentStep === 'questions' ? currentCategoryIndex : -1}
          currentStep={currentStep}
          currentQuestionIndex={currentQuestionIndex}
          responses={responses}
          onNavigateToQuestion={handleNavigateToQuestion}
        >
          {currentStep === 'questions' && (
            <AssessmentProgress
              percentage={progressPercentage}
              currentCategory={currentCategory?.name}
              currentQuestionIndex={currentQuestionIndex >= 0 ? currentQuestionIndex : undefined}
              totalQuestions={currentCategoryQuestionsCount}
              completedQuestions={currentCategoryCompletedCount}
            />
          )}

          <div className={currentStep !== 'welcome' ? 'mt-4' : ''}>
            <AssessmentContent
              currentStep={currentStep}
              assessment={assessment}
              categories={sortedCategories}
              currentCategoryIndex={currentCategoryIndex}
              currentQuestionIndex={currentQuestionIndex}
              responses={responses}
              scoresByCategory={calculatedScores}
              personalInfo={personalInfo}
              onStart={handleStartAssessment}
              onRestart={handleFullRestart}
              onExit={onBack}
              onBack={moveToPreviousQuestion}
              onNext={handleMoveToNextQuestion}
              onResponseChange={handleResponseChange}
              onPersonalInfoSubmit={async () => {}}
              canMoveForward={currentQuestion ? canMoveForward(currentQuestion) : true}
              isSubmitting={isSubmitting}
              isPremium={true}
            />
          </div>
        </AssessmentLayout>
      </div>
    </div>
  );
};

/** Get ALL categories with non-disabled questions (both free and premium).
 *  Within each category, free questions come first, then premium questions. */
function getAllCategoriesWithQuestions(assessment: Assessment): Category[] {
  const categories = assessment.categories
    .filter((c) => c.questions.some((q) => !q.disabled))
    .map((c) => ({
      ...c,
      questions: [...c.questions.filter((q) => !q.disabled)].sort((a, b) => {
        // Free questions first (premium=false), then premium questions (premium=true)
        if (a.premium !== b.premium) return a.premium ? 1 : -1;
        return 0; // Keep original order within same type
      }),
    }));

  return [...categories].sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
    return 0;
  });
}

export default PremiumAssessment;
